import React, { useState, useEffect, useCallback } from 'react';
import ItemGrid from './ItemGrid';
import ItemModal from './ItemModal';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import { useModal } from '../../../hooks/useModal';
import { useDebounce } from '../../../hooks/useDebounce';
import * as itemService from '../../../services/itemService';

/**
 * Self-contained Items feature view.
 *
 * Slightly more complex than Customers because Items need
 * dependency data (categories, suppliers, taxes) for the modal dropdowns.
 * The itemService.fetchItems() returns all of it in one call.
 */
export default function ItemsView() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await itemService.fetchItems(debouncedSearchTerm);
      setItems(result.items);
      setCategories(result.categories);
      setSuppliers(result.suppliers);
      setTaxes(result.taxes);
    } catch (err) {
      console.error('Error fetching items:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      if (editModal.record) {
        await itemService.updateItem(editModal.record.item_id, formData);
      } else {
        // Duplicate check
        if (items.some((r) => r.item_id === formData.item_id)) {
          alert(`Error: A record with ID "${formData.item_id}" already exists.`);
          return;
        }
        await itemService.createItem(formData);
      }
      editModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await itemService.deleteItem(deleteModal.record.item_id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search items..."
      />
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <ItemGrid
            rowData={items}
            onEdit={(r) => editModal.open(r)}
            onDelete={(r) => deleteModal.open(r)}
            onGridReady={setGridApi}
          />
        )}
      </div>
      <ItemModal
        isOpen={editModal.isOpen}
        onClose={editModal.close}
        onSubmit={handleSave}
        initialData={editModal.record}
        title={editModal.record ? 'Edit Item' : 'Add New Item'}
        categories={categories}
        suppliers={suppliers}
        taxes={taxes}
      />
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={deleteModal.record?.sales_description || deleteModal.record?.item_id || 'this record'}
      />
    </>
  );
}
